// Post-hoc workout editing — the arithmetic behind History's flat rep table.
//
// Pure functions only: no React, no network, loadable by the probe shim the
// same way burn.js is. SwimmingHistory owns the table UI; this module owns
// every number the save writes.
//
// THE INVARIANT. Sets and the window it implies are two views of one pass:
// buildWorkoutPatch returns them as a single object, and the save handler
// gives that object — whole — to updateItem. There is no writer that accepts
// Sets and the window separately, so they cannot diverge. This is the
// structural fix for the 8/4/2026 red bar: rep times patched into Sets
// through chat left WorkoutEndTime where the original save put it, and
// computeMoving read swim time greater than the session it happened in.
//
// ActiveSeconds is WALL-CLOCK: everything the row records — swim plus rest —
// plus the slack the taps around it cost. That makes ActiveSeconds,
// DurationSeconds and (WorkoutEndTime − WorkoutStartTime) the same number
// wearing three column names, which is exactly the property the stamps had on
// a clean row. computeMoving prefers ActiveSeconds when present, so an edited
// row reads precisely as a clean row does; rows never edited lack the column
// and fall through to the stamps unchanged.

import { YARDS_TO_METERS } from './fitnessApi';

// The gap between recorded work and the stamps on clean rows runs 15-80s
// (walk to the phone, the Done tap, the summary screen); 30 is its midpoint.
export const EDIT_SLACK_SECONDS = 30;

// The complete field object for updateItem, from the edited sets. Throws
// rather than returns on anything unwritable — a patch that cannot hold the
// invariant must not exist at all.
//
// Per timed set, recomputed from its (possibly edited) repTimes:
//   actualReps      = repTimes.length — keeps SwimmingDashboard's pace check
//                     and movingSecondsFromSets' completeness check satisfied
//   timeSeconds     = min(repTimes), the writer convention
//                     (SwimmingWorkout.js:473, :608)
//   distanceMeters  = per-rep distance × actualReps, converted from poolUnit
//   repRests / repRestTargets padded to repTimes.length — parallel arrays are
//                     a writer guarantee readers rely on
//
// A set with no timed reps passes through untouched: its stored distance
// stands, and it contributes nothing to the window. `partial` flags are never
// altered — "cut short on purpose" is a fact about the swim, not the edit.
export function buildWorkoutPatch({ workoutStartTime, poolUnit, sets }) {
  const startMs = Date.parse(workoutStartTime);
  if (!Number.isFinite(startMs)) {
    throw new Error('Cannot edit a workout with no start stamp — there is no window to correct.');
  }

  let recorded = 0;
  let totalMeters = 0;

  const rebuilt = sets.map((set, i) => {
    const timed = Array.isArray(set.repTimes) && set.repTimes.length > 0;
    if (!timed) {
      totalMeters += Number(set.distanceMeters) || 0;
      return set;
    }

    // Times round to 0.1s — the stopwatch's own precision; a typed 113.456
    // must not out-resolve a tapped 113.4.
    const repTimes = set.repTimes.map(t => Math.round(Number(t) * 10) / 10);
    if (repTimes.some(t => !Number.isFinite(t) || t <= 0)) {
      throw new Error(`Set ${i + 1}: every rep needs a time greater than zero.`);
    }

    const pad = arr => {
      const out = Array.isArray(arr) ? arr.slice(0, repTimes.length) : [];
      while (out.length < repTimes.length) out.push(null);
      return out;
    };
    // A rest is a positive number or null — never '', never 0. An emptied
    // input means "no rest recorded", which is null by the house rule that
    // absence is not zero.
    const repRests = pad(set.repRests).map(r => {
      const n = Number(r);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 10) / 10 : null;
    });
    const repRestTargets = pad(set.repRestTargets);

    for (const t of repTimes) recorded += t;
    for (const r of repRests) {
      const n = Number(r);
      if (Number.isFinite(n) && n > 0) recorded += n;
    }

    const actualReps = repTimes.length;
    const perRep = Number(set.actualDistance ?? set.distance) || 0;
    const distanceMeters = poolUnit === 'yd'
      ? perRep * actualReps * YARDS_TO_METERS
      : perRep * actualReps;
    totalMeters += distanceMeters;

    return {
      ...set,
      repTimes,
      repRests,
      repRestTargets,
      actualReps,
      timeSeconds: Math.min(...repTimes),
      distanceMeters,
    };
  });

  if (recorded <= 0) {
    throw new Error('No timed work left — delete the workout instead of editing it empty.');
  }

  const activeSeconds = Math.round(recorded + EDIT_SLACK_SECONDS);

  return {
    Sets: JSON.stringify(rebuilt),
    ActiveSeconds: activeSeconds,
    DurationSeconds: activeSeconds,
    WorkoutEndTime: new Date(startMs + activeSeconds * 1000).toISOString(),
    TotalDistanceMeters: Math.round(totalMeters),
  };
}
